import { t as uiText } from '../i18n';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import {
  directAddMemberErrorMessage,
  memberLimitInfo,
  memberLimitNoticeText,
  type MemberLimitInfo,
} from '../features/workspace/memberInviteMessages';
import PlanLimitNotice from './PlanLimitNotice';

type Props = {
  onClose: () => void;
};

const InviteMemberModal: React.FC<Props> = ({ onClose }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [messageIsError, setMessageIsError] = useState(false);
  const [limitInfo, setLimitInfo] = useState<MemberLimitInfo | null>(null);
  const [sending, setSending] = useState(false);

  // メールアドレスの形式をチェックする関数
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInvite = async () => {
    setMessage(null);
    setLimitInfo(null);
    if (!email || !routeWorkspaceId) return;
    setSending(true);

    try {
      // Firebase Functionsを呼び出し
      const inviteUserToWorkspace = httpsCallable(functions, 'inviteUserToWorkspaceFn');
      await inviteUserToWorkspace({
        email: email,
        workspaceId: routeWorkspaceId
      });

      setMessageIsError(false);
      setMessage(uiText("Member added."));
      setEmail('');
    } catch (error: unknown) {
      console.error('追加に失敗:', error);
      setMessageIsError(true);
      const info = memberLimitInfo(error);
      if (info) {
        setLimitInfo(info);
        return;
      }
      setMessage(directAddMemberErrorMessage(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          width: '400px',
          padding: '20px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: 'var(--workspace-primary, #cfae91)' }}>
          {uiText("Add a member directly")}
        </div>

        <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
          {uiText("Add a member directly by specifying their notreu account email address")}<br />
          {uiText("※ The person you add must already have a notreu account")}
        </p>

        <input
          type="email"
          placeholder={uiText("Enter email address")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            marginBottom: '12px',
            borderRadius: '4px',
            border: '1px solid #ccc',
            outline: 'none',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => {
            e.target.style.border = '2px solid var(--workspace-primary, #cfae91)';
          }}
          onBlur={(e) => {
            e.target.style.border = '1px solid #ccc';
          }}
        />

        <button
          onClick={handleInvite}
          disabled={sending || !routeWorkspaceId || !email.trim() || !isValidEmail(email)}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: sending || !routeWorkspaceId || !email.trim() || !isValidEmail(email) ? '#ccc' : 'var(--workspace-primary, #cfae91)',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontWeight: 'bold',
            cursor: sending || !routeWorkspaceId || !email.trim() || !isValidEmail(email) ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? uiText("Adding...") : uiText("Add")}
        </button>

        {limitInfo && (
          // このモーダルは管理者（owner / admin）だけが開ける
          <PlanLimitNotice
            kind="members"
            isAdmin
            {...memberLimitNoticeText(limitInfo)}
            extraActions={
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 14px',
                  borderRadius: 6,
                  border: '1px solid var(--workspace-primary, #cfae91)',
                  background: '#fff',
                  color: 'var(--workspace-primary, #cfae91)',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {uiText('Manage members')}
              </button>
            }
          />
        )}

        {message && (
          <p style={{ 
            marginTop: '12px', 
            fontSize: '14px', 
            color: messageIsError ? 'red' : 'green' 
          }}>
            {message}
          </p>
        )}

        <button
          onClick={onClose}
          style={{
            marginTop: '16px',
            display: 'block',
            width: '100%',
            background: 'none',
            border: 'none',
            color: 'var(--workspace-primary, #cfae91)',
            cursor: 'pointer',
          }}
        >
          {uiText("Close")}
        </button>
      </div>
    </div>
  );
};

export default InviteMemberModal;
